import { useState } from 'react';
import styles from './MoodRow.module.scss';
import { values } from '../CheckIn/checkInValues';
import ModalContainer from '../components/ModalContainer';
import Tooltip from '../components/Tooltip';
import ExtraCheckInModal from './ExtraCheckInModal';
import { JournalData, MiddleSchoolCheckInEntry } from '../../peekapak-types/ReportDataExchangeTypes';

interface Props {
  studentName: string;
  isShowStudents: boolean;
  data?: JournalData['Progress'];
  dateRange: Date[];
}

interface ModalContent {
  title: string;
  checkIns: MiddleSchoolCheckInEntry[];
}

const MoodRow = ({ studentName, isShowStudents, data, dateRange }: Props): JSX.Element => {
  const [isShowModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    title: '',
    checkIns: [],
  });
  const entriesFilteredByDate = data?.filter((item) => {
    const dateRangeCopy = dateRange.map((date) => new Date(date));
    const itemDate = new Date(item.createdAt);
    return (
      itemDate > dateRange[0] && itemDate < new Date(dateRangeCopy[dateRangeCopy.length - 1].setHours(23, 59, 59, 999))
    );
  });

  const checkInArray = entriesFilteredByDate
    ? dateRange.reduce(
        (acc, curr: Date, index) => {
          acc[index] =
            entriesFilteredByDate.filter((item) => {
              const itemDate = new Date(item.createdAt);
              if (index < dateRange.length - 1) return itemDate >= curr && itemDate < dateRange[index + 1];
              if (index === dateRange.length - 1) return itemDate > curr;
            }) || [];
          return acc;
        },
        [] as JournalData['Progress'][],
      )
    : new Array(dateRange.length).fill([]);

  const getTextColor = (feeling: string | undefined) => {
    if (feeling === undefined) return '#86939e';
    if (values.findIndex((value) => value.subEmotions.includes(feeling)) < 2) return '#43484d';
    return '#fff';
  };

  const hideModal = () => setShowModal(false);

  const populateModal = (checkInArray: MiddleSchoolCheckInEntry[]) => {
    if (checkInArray.length === 0) return;
    setModalContent({
      title: 'Additional Check-Ins',
      checkIns: checkInArray,
    });
    setShowModal(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.nameCell}>
        <h4>{isShowStudents ? studentName : 'Private'}</h4>
      </div>

      <div className={styles.unitDataContainer}>
        {checkInArray.map((checkInDay, index) => {
          checkInDay.length > 1 &&
            checkInDay.sort((a: MiddleSchoolCheckInEntry, b: MiddleSchoolCheckInEntry) => b.createdAt - a.createdAt);
          const emotionColor =
            values.find((value) => value.subEmotions.includes(checkInDay[0]?.feeling))?.segmentColor || '#bbc2ca';
          return (
            <div
              key={`${studentName}${index} activity.id`}
              className={styles.dataCell}
              style={{
                backgroundColor: emotionColor,
              }}
            >
              <h4
                style={{
                  color: getTextColor(checkInDay[0]?.feeling),
                }}
              >
                {checkInDay[0]?.feeling.replace('/', ' / ') || '-'}
              </h4>

              {checkInDay.length > 1 && (
                <div className={styles.modalButtonContainer}>
                  <button className={styles.modalButton} onClick={() => populateModal(checkInDay)}>
                    <Tooltip
                      direction='top'
                      arrowPosition='right-align'
                      content={`Student checked in more than once on this day. Click to show additional check-ins.`}
                      color='white'
                    >
                      i
                    </Tooltip>
                  </button>
                </div>
              )}

              {checkInDay.length > 1 && isShowModal && (
                <ModalContainer hideModal={hideModal}>
                  <ExtraCheckInModal
                    hideModal={hideModal}
                    checkIns={modalContent.checkIns}
                    title={modalContent.title}
                  />
                </ModalContainer>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MoodRow;
